import React, { useMemo, useState } from "react";
import { useRoom } from "./RoomProvider";

/**
 * FloorDialer — the general-purpose "call anyone" button.
 *
 * Before this, the ONLY way to place a call from The Floor was per-trip
 * Call buttons (rider/driver/TP on a focused trip) or the hidden "/" command
 * line's "call <driver name>" verb — no visible control existed to ring an
 * on-shift driver who isn't attached to a live trip, or a raw phone number
 * at all. This panel fixes that: pick a driver from the full on-shift
 * roster, or type any number, and ring it through the same SignalWire
 * breakout room every other Floor call uses.
 */
export default function FloorDialer() {
  const { dialerOpen, setDialerOpen, allDrivers, callSession } = useRoom();
  const [query, setQuery] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState(null);

  const onShift = useMemo(() => {
    const rows = (allDrivers || []).filter((d) => {
      const s = String(d.shift_status || "").toLowerCase();
      return s && s !== "offline";
    });
    const q = query.trim().toLowerCase();
    return (q ? rows.filter((d) => (d.name || "").toLowerCase().includes(q)) : rows).slice(0, 20);
  }, [allDrivers, query]);

  if (!dialerOpen) return null;

  const close = () => { setDialerOpen(false); setQuery(""); setPhone(""); setNote(null); };

  const ringDriver = async (d) => {
    setNote(`ringing ${d.name}…`);
    const res = await callSession?.start({
      targets: [{ kind: "driver", id: d.driver_id, name: d.name }],
      purpose: "manual_dial",
    });
    if (res?.ok) close();
    else setNote(res?.error || "call failed to start");
  };

  const ringNumber = async () => {
    const digits = phone.replace(/[^\d+]/g, "");
    if (digits.replace(/\D/g, "").length < 10) { setNote("enter a valid phone number"); return; }
    setNote(`dialing ${phone}…`);
    const res = await callSession?.start({
      targets: [{ kind: "external", phone: digits, name: phone }],
      purpose: "manual_dial",
    });
    if (res?.ok) close();
    else setNote(res?.error || "call failed to start");
  };

  return (
    <div className="cmd-scrim" data-testid="floor-dialer-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="dialer-panel" data-testid="floor-dialer-panel">
        <div className="dialer-head">
          <span>☎ Dialer</span>
          <button className="dialer-close" onClick={close} data-testid="floor-dialer-close-btn">✕</button>
        </div>

        <div className="dialer-section">
          <div className="dialer-label">On-shift drivers</div>
          <input
            autoFocus
            className="dialer-input"
            placeholder="search a driver by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="floor-dialer-driver-search"
          />
          <div className="dialer-driver-list">
            {onShift.length === 0 && <div className="dialer-empty">no on-shift drivers match</div>}
            {onShift.map((d) => (
              <button
                key={d.driver_id}
                className="dialer-driver-row"
                onClick={() => ringDriver(d)}
                disabled={callSession?.starting}
                data-testid={`floor-dialer-call-driver-${d.driver_id}`}
              >
                <span className="dialer-driver-name">{d.name}</span>
                <span className="dialer-driver-status">{d.shift_status || "on shift"}</span>
                <span className="dialer-driver-ring">Call →</span>
              </button>
            ))}
          </div>
        </div>

        <div className="dialer-section">
          <div className="dialer-label">Or dial a number</div>
          <div className="dialer-phone-row">
            <input
              className="dialer-input"
              placeholder="+1 555 555 5555"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ringNumber(); }}
              data-testid="floor-dialer-phone-input"
            />
            <button
              className="dialer-call-btn"
              onClick={ringNumber}
              disabled={callSession?.starting}
              data-testid="floor-dialer-call-number-btn"
            >
              Call
            </button>
          </div>
        </div>

        {note && <div className="dialer-note" data-testid="floor-dialer-note">{note}</div>}
      </div>
    </div>
  );
}
